// Shared organization activity feed helpers (split from the component for react-refresh).
import { KEYS } from '@/features/apiKeys/catalog';
import type { OrgKeyName, OrgUpdate } from '@/types';

export interface FeedMark {
  icon: string;
  color: string;
  mix: string;
}

/** One icon and tint per update kind. Every row of that kind reuses the same mark. */
const FEED_MARKS: Record<string, FeedMark> = {
  member_added: { icon: 'bi-person-plus-fill', color: 'var(--color-success)', mix: '16%' },
  member_removed: { icon: 'bi-person-dash-fill', color: 'var(--color-danger)', mix: '16%' },
  member_suspended: { icon: 'bi-person-slash', color: 'var(--color-warning)', mix: '20%' },
  role_changed: { icon: 'bi-person-gear', color: 'var(--color-primary)', mix: '16%' },
  seat_increase_requested: { icon: 'bi-plus-circle', color: 'var(--color-info)', mix: '18%' },
  seat_decrease_requested: { icon: 'bi-dash-circle', color: 'var(--color-warning)', mix: '34%' },
  seat_change_applied: { icon: 'bi-people-fill', color: 'var(--color-success)', mix: '32%' },
  seat_request_rejected: { icon: 'bi-x-circle', color: 'var(--color-danger)', mix: '32%' },
  domain_added: { icon: 'bi-globe2', color: 'var(--color-info)', mix: '32%' },
  domain_removed: { icon: 'bi-globe', color: 'var(--color-danger)', mix: '22%' },
  join_code_rotated: { icon: 'bi-arrow-repeat', color: 'var(--color-secondary-deep)', mix: '24%' },
  default_key_updated: { icon: 'bi-key', color: 'var(--color-primary)', mix: '28%' },
  quralyst_api_keys_updated: { icon: 'bi-shield-lock', color: 'var(--brand-navy)', mix: '14%' },
  org_suspended: { icon: 'bi-pause-circle', color: 'var(--color-warning)', mix: '16%' },
  org_reactivated: { icon: 'bi-play-circle', color: 'var(--color-success)', mix: '22%' },
  invite_sent: { icon: 'bi-envelope', color: 'var(--color-info)', mix: '14%' },
  tier_changed: { icon: 'bi-layers', color: 'var(--color-primary)', mix: '22%' },
  credit_adjustment: { icon: 'bi-coin', color: 'var(--color-warning)', mix: '28%' },
  user_promoted: { icon: 'bi-arrow-up-circle', color: 'var(--color-success)', mix: '40%' },
  admin_revoked: { icon: 'bi-shield-x', color: 'var(--color-danger)', mix: '40%' },
  user_approved: { icon: 'bi-person-check', color: 'var(--color-success)', mix: '12%' },
  user_rejected: { icon: 'bi-person-x', color: 'var(--color-danger)', mix: '12%' },
  org_approved: { icon: 'bi-building-check', color: 'var(--color-info)', mix: '40%' },
  org_rejected: { icon: 'bi-building-x', color: 'var(--color-primary)', mix: '36%' },
};

const DEFAULT_MARK: FeedMark = { icon: 'bi-bell', color: 'var(--color-primary)', mix: '16%' };

export function feedMark(updateType: string): FeedMark {
  const type = updateType.toLowerCase();
  const exact = FEED_MARKS[type];
  if (exact) return exact;
  if (type.includes('billing') || type.includes('invoice') || type.includes('credit')) {
    return FEED_MARKS.credit_adjustment;
  }
  if (type.includes('member_added') || type.includes('joined') || type.includes('join_request')) {
    return FEED_MARKS.member_added;
  }
  if (type.includes('invite')) return FEED_MARKS.invite_sent;
  if (type.includes('role')) return FEED_MARKS.role_changed;
  if (type.includes('key')) return FEED_MARKS.quralyst_api_keys_updated;
  return DEFAULT_MARK;
}

export function feedByline(row: OrgUpdate): string {
  const name = (row.actorName || '').trim();
  const email = (row.actorEmail || '').trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `By ${name} (${email})`;
  }
  if (name) return `By ${name}`;
  if (email) return `By ${email}`;
  return '';
}

export function feedKeyLabels(keys: string[] | undefined): string {
  if (!keys?.length) return '';
  return keys.map((key) => KEYS[key as OrgKeyName]?.label ?? key.replace(/_/g, ' ')).join(', ');
}
